import React, {FC} from 'react';

import {copySelectionToClipboard} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {RepositionPopover} from '../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {SelectionLocation} from '../../../helpers/domSelectionHelpers';
import {
  buildHighlightAnnotation,
  buildHighlightCommentAnnotation,
} from '../../../helpers/models/annotationHelpers';
import {AnnotationHighlightCommentModel, AnnotationHighlightModel} from '../../../models/api/annotationModel';
import {FloatingToolbar} from '../floatingBars/floatingToolbar';
import {FloatingToolbarButton} from '../floatingBars/floatingToolbarButton';

export interface AnnotationHighlightViewerToolbarProps {
  currentLocation: SelectionLocation;
  onQuoteInCommentClick: HandlerOf<AnnotationHighlightModel | AnnotationHighlightCommentModel>;
  onRequestClose: Handler;
  isAnnotatingComment?: boolean;
}

interface KeyboardShortcutHandlers {
  [KeyboardActionsEnum.COPY]: Handler;
  [KeyboardActionsEnum.CLOSE]: Handler;
}

export const AnnotationHighlightViewerToolbar: FC<AnnotationHighlightViewerToolbarProps> = (props) => {
  const {currentLocation, onRequestClose, isAnnotatingComment} = props;

  const {window} = useLayer();

  function copySelectionAndClose() {
    copySelectionToClipboard(window);

    onRequestClose();
  }

  function onQuoteInComment() {
    const annotataion = props.isAnnotatingComment
      ? buildHighlightCommentAnnotation(currentLocation)
      : buildHighlightAnnotation(currentLocation);

    props.onQuoteInCommentClick(annotataion);

    onRequestClose();
  }

  // Close the toolbar on copy so we don't get in the way.
  const keyboardShortcutHandlers = {
    [KeyboardActionsEnum.COPY]: copySelectionAndClose,
    [KeyboardActionsEnum.CLOSE]: onRequestClose,
  };

  return (
    <RepositionPopover
      anchor={currentLocation.anchor}
      placement="bottom-end"
      onRequestClose={onRequestClose}
      isExclusive={false}
      isInline
    >
      {isAnnotatingComment
        ? renderReplyButton(keyboardShortcutHandlers, onQuoteInComment)
        : renderCommentButton(keyboardShortcutHandlers, onQuoteInComment)}
    </RepositionPopover>
  );
};

function renderCommentButton(keyboardShortcutHandlers: KeyboardShortcutHandlers, onQuoteInComment: Handler) {
  return (
    <FloatingToolbar size={VisualSizesEnum.MEDIUM}>
      <KeyboardShortcuts handlers={keyboardShortcutHandlers} />
      <FloatingToolbarButton
        iconName="discussionFilledSmall"
        label={<Trans id="annotations-viewer-comment">Comment</Trans>}
        size={VisualSizesEnum.MEDIUM}
        onClick={onQuoteInComment}
        shouldRenderLabelInline
      />
    </FloatingToolbar>
  );
}

function renderReplyButton(keyboardShortcutHandlers: KeyboardShortcutHandlers, onQuoteInComment: Handler) {
  return (
    <FloatingToolbar size={VisualSizesEnum.MEDIUM}>
      <KeyboardShortcuts handlers={keyboardShortcutHandlers} />
      <FloatingToolbarButton
        iconName="replySmall"
        label={<Trans id="annotations-viewer-reply">Reply</Trans>}
        size={VisualSizesEnum.MEDIUM}
        onClick={onQuoteInComment}
        shouldRenderLabelInline
      />
    </FloatingToolbar>
  );
}
